// src/api/Retail.js

import { http } from "../lib/http";
import { normalizeWordPressUserID } from "../Utils/UserIdentity";

/* -------------------------------- helpers -------------------------------- */

function normalizeText(value) {
  return String(value ?? "").trim();
}

function normalizeEmail(value) {
  return normalizeText(value).toLowerCase();
}

function normalizeBoolean(value) {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "number") {
    return value === 1;
  }

  const normalized = normalizeText(value).toLowerCase();

  return ["1", "true", "yes", "on"].includes(
    normalized,
  );
}

/**
 * Safely encode a REST route segment.
 *
 * WordPress user IDs may be GraphQL base64 IDs.
 * Encoded slashes are double-encoded so Apache does not interpret them
 * as route separators before WordPress receives the request.
 */
function encodePathSegment(value) {
  const encoded = encodeURIComponent(
    normalizeText(value),
  );

  return encoded.replace(/%2F/gi, "%252F");
}

/**
 * Build the payload expected by:
 *
 * POST /wp-json/api/retail
 *
 * The agreement checkbox is intentionally excluded because it is only
 * frontend confirmation before the request is submitted.
 *
 * The current frontend language is also intentionally excluded because
 * Retail dropdowns are stored using stable internal values.
 */
function buildRetailPayload(payload = {}) {
  const normalizedUserID =
    normalizeWordPressUserID(payload.userID);

  return {
    userID: normalizedUserID || null,

    /* ---------------- person/contact ---------------- */

    name: normalizeText(payload.name),

    usageLocation: normalizeText(
      payload.usageLocation,
    ),

    endUser: normalizeText(payload.endUser),

    repairAvailableTime: normalizeText(
      payload.repairAvailableTime,
    ),

    landlineAreaCode: normalizeText(
      payload.landlineAreaCode,
    ),

    landlinePhoneNumber: normalizeText(
      payload.landlinePhoneNumber,
    ),

    mobileNumber: normalizeText(
      payload.mobileNumber,
    ),

    email: normalizeEmail(payload.email),

    city: normalizeText(payload.city),
    district: normalizeText(payload.district),
    street: normalizeText(payload.street),
    lane: normalizeText(payload.lane),
    alley: normalizeText(payload.alley),

    streetNumber: normalizeText(
      payload.streetNumber,
    ),

    floor: normalizeText(payload.floor),

    otherAddress: normalizeText(
      payload.otherAddress,
    ),

    addToMember: normalizeBoolean(
      payload.addToMember,
    ),

    /* ---------------- repair information ---------------- */

    equipmentName: normalizeText(
      payload.equipmentName,
    ),

    customEquipmentNameOrModel: normalizeText(
      payload.customEquipmentNameOrModel,
    ),

    modelName: normalizeText(
      payload.modelName,
    ),

    serialNumber: normalizeText(
      payload.serialNumber,
    ),

    purchaseDate: normalizeText(
      payload.purchaseDate,
    ),

    shippingDate: normalizeText(
      payload.shippingDate,
    ),

    installationDate: normalizeText(
      payload.installationDate,
    ),

    installer: normalizeText(
      payload.installer,
    ),

    installationSource: normalizeText(
      payload.installationSource,
    ),

    customInstallationSource: normalizeText(
      payload.customInstallationSource,
    ),

    faultReason: normalizeText(
      payload.faultReason,
    ),

    customFaultReason: normalizeText(
      payload.customFaultReason,
    ),

    repairNotes: normalizeText(
      payload.repairNotes,
    ),
  };
}

/**
 * Normalize the successful three-table Retail response.
 */
function normalizeRetailResponse(response) {
  const data = response?.data ?? response ?? {};

  return {
    ...data,

    ok: data?.ok === true,

    message: normalizeText(data?.message),

    retailID: normalizeText(
      data?.retailID,
    ),

    retailPersonID: normalizeText(
      data?.retailPersonID,
    ),

    retailInfoID: normalizeText(
      data?.retailInfoID,
    ),

    userID:
      data?.userID === null ||
      data?.userID === undefined
        ? null
        : data.userID,

    status: normalizeText(data?.status),

    createdAt: normalizeText(
      data?.createdAt,
    ),
  };
}

/**
 * Normalize one Retail History row.
 */
function normalizeRetailHistoryItem(item) {
  if (
    !item ||
    typeof item !== "object"
  ) {
    return null;
  }

  const retailID = normalizeText(
    item.retailID,
  );

  if (!retailID) {
    return null;
  }

  const person =
    item.person &&
    typeof item.person === "object"
      ? item.person
      : {};

  const info =
    item.info &&
    typeof item.info === "object"
      ? item.info
      : {};

  return {
    retailID,

    retailPersonID: normalizeText(
      item.retailPersonID,
    ),

    retailInfoID: normalizeText(
      item.retailInfoID,
    ),

    status: normalizeText(
      item.status,
    ),

    createdAt: normalizeText(
      item.createdAt,
    ),

    updatedAt: normalizeText(
      item.updatedAt,
    ),

    person: {
      userID:
        person.userID === null ||
        person.userID === undefined
          ? null
          : person.userID,

      name: normalizeText(
        person.name,
      ),

      usageLocation: normalizeText(
        person.usageLocation,
      ),

      endUser: normalizeText(
        person.endUser,
      ),

      repairAvailableTime: normalizeText(
        person.repairAvailableTime,
      ),

      landlineAreaCode: normalizeText(
        person.landlineAreaCode,
      ),

      landlinePhoneNumber: normalizeText(
        person.landlinePhoneNumber,
      ),

      mobileNumber: normalizeText(
        person.mobileNumber,
      ),

      email: normalizeText(
        person.email,
      ),

      city: normalizeText(
        person.city,
      ),

      district: normalizeText(
        person.district,
      ),

      street: normalizeText(
        person.street,
      ),

      lane: normalizeText(
        person.lane,
      ),

      alley: normalizeText(
        person.alley,
      ),

      streetNumber: normalizeText(
        person.streetNumber,
      ),

      floor: normalizeText(
        person.floor,
      ),

      otherAddress: normalizeText(
        person.otherAddress,
      ),

      addToMember: normalizeBoolean(
        person.addToMember,
      ),
    },

    info: {
      equipmentName: normalizeText(
        info.equipmentName,
      ),

      customEquipmentNameOrModel:
        normalizeText(
          info.customEquipmentNameOrModel,
        ),

      modelName: normalizeText(
        info.modelName,
      ),

      serialNumber: normalizeText(
        info.serialNumber,
      ),

      purchaseDate: normalizeText(
        info.purchaseDate,
      ),

      shippingDate: normalizeText(
        info.shippingDate,
      ),

      installationDate: normalizeText(
        info.installationDate,
      ),

      installer: normalizeText(
        info.installer,
      ),

      installationSource: normalizeText(
        info.installationSource,
      ),

      customInstallationSource:
        normalizeText(
          info.customInstallationSource,
        ),

      faultReason: normalizeText(
        info.faultReason,
      ),

      customFaultReason: normalizeText(
        info.customFaultReason,
      ),

      repairNotes: normalizeText(
        info.repairNotes,
      ),
    },
  };
}

/**
 * Normalize a Retail History result list.
 */
function normalizeRetailHistoryList(items) {
  if (!Array.isArray(items)) {
    return [];
  }

  return items
    .map(normalizeRetailHistoryItem)
    .filter(Boolean);
}

/**
 * Normalize one Retail progress comment.
 */
function normalizeRetailComment(item) {
  if (
    !item ||
    typeof item !== "object"
  ) {
    return null;
  }

  const retailID = normalizeText(
    item.retailID,
  );

  const commentContent = normalizeText(
    item.commentContent,
  );

  if (
    !retailID ||
    !commentContent
  ) {
    return null;
  }

  const parsedCommentID = Number.parseInt(
    item.commentID,
    10,
  );

  const rawAuthorType = normalizeText(
    item.authorType,
  ).toLowerCase();

  const authorType = [
    "admin",
    "user",
  ].includes(rawAuthorType)
    ? rawAuthorType
    : "";

  return {
    commentID: Number.isFinite(
      parsedCommentID,
    )
      ? parsedCommentID
      : 0,

    retailID,

    authorType,

    commentContent,

    createdAt: normalizeText(
      item.createdAt ??
        item.created_at,
    ),

    updatedAt: normalizeText(
      item.updatedAt ??
        item.updated_at,
    ),
  };
}

/**
 * Normalize a Retail comment list.
 *
 * The backend already returns comments from oldest to newest.
 */
function normalizeRetailCommentList(items) {
  if (!Array.isArray(items)) {
    return [];
  }

  return items
    .map(normalizeRetailComment)
    .filter(Boolean);
}

/**
 * Normalize:
 *
 * GET /wp-json/api/retail/comments
 */
function normalizeRetailCommentsResponse(
  response,
) {
  const data =
    response?.data ?? response ?? {};

  const comments =
    normalizeRetailCommentList(
      data?.comments,
    );

  const parsedCount = Number.parseInt(
    data?.count,
    10,
  );

  return {
    ...data,

    ok: data?.ok === true,

    retailID: normalizeText(
      data?.retailID,
    ),

    count: Number.isFinite(
      parsedCount,
    )
      ? parsedCount
      : comments.length,

    comments,
  };
}

/**
 * Normalize:
 *
 * POST /wp-json/api/retail/comment
 */
function normalizeRetailCommentCreateResponse(
  response,
) {
  const data =
    response?.data ?? response ?? {};

  return {
    ...data,

    ok: data?.ok === true,

    message: normalizeText(
      data?.message,
    ),

    retailID: normalizeText(
      data?.retailID,
    ),

    comment: normalizeRetailComment(
      data?.comment,
    ),
  };
}

/**
 * Normalize a Retail cancellation response.
 */
function normalizeRetailCancelResponse(response) {
  const data = response?.data ?? response ?? {};

  return {
    ...data,

    ok: data?.ok === true,

    message: normalizeText(
      data?.message,
    ),

    retailID: normalizeText(
      data?.retailID,
    ),

    status: normalizeText(
      data?.status,
    ),

    updatedAt: normalizeText(
      data?.updatedAt,
    ),

    item: normalizeRetailHistoryItem(
      data?.item,
    ),
  };
}

/* ---------------------------------- API ---------------------------------- */

export const RetailAPI = {
  /**
   * Create one online-repair request.
   *
   * Backend:
   * POST /wp-json/api/retail
   */
  async create(payload = {}) {
    const body = buildRetailPayload(payload);

    const response = await http.post(
      "/retail",
      body,
    );

    return normalizeRetailResponse(response);
  },

  /**
   * Load all Retail requests belonging to one WordPress user.
   *
   * Backend:
   * GET /wp-json/api/retail/history/{userID}
   */
  async getHistory(userID) {
    const normalizedUserID =
      normalizeWordPressUserID(userID);

    if (!normalizedUserID) {
      return [];
    }

    const encodedUserID =
      encodePathSegment(normalizedUserID);

    const response = await http.get(
      `/retail/history/${encodedUserID}`,
    );

    const data =
      response?.data ?? response ?? {};

    return normalizeRetailHistoryList(
      data?.items,
    );
  },

  /**
   * Load all comments belonging to one Retail request.
   *
   * The backend verifies that the Retail request belongs
   * to the supplied WordPress user.
   *
   * Backend:
   * GET /wp-json/api/retail/comments
   */
  async getComments(
    retailID,
    userID,
  ) {
    const normalizedRetailID =
      normalizeText(retailID);

    const normalizedUserID =
      normalizeWordPressUserID(userID);

    if (
      !normalizedRetailID ||
      !normalizedUserID
    ) {
      throw new Error(
        "retailID and userID are required",
      );
    }

    const response = await http.get(
      "/retail/comments",
      {
        params: {
          retailID:
            normalizedRetailID,

          userID:
            normalizedUserID,
        },
      },
    );

    return normalizeRetailCommentsResponse(
      response,
    );
  },

  /**
   * Add one customer comment to a Retail request.
   *
   * The backend verifies that the Retail request belongs
   * to the supplied WordPress user before inserting it.
   *
   * Backend:
   * POST /wp-json/api/retail/comment
   */
  async addComment(
    retailID,
    userID,
    commentContent,
  ) {
    const normalizedRetailID =
      normalizeText(retailID);

    const normalizedUserID =
      normalizeWordPressUserID(userID);

    const normalizedCommentContent =
      normalizeText(commentContent);

    if (
      !normalizedRetailID ||
      !normalizedUserID
    ) {
      throw new Error(
        "retailID and userID are required",
      );
    }

    if (!normalizedCommentContent) {
      throw new Error(
        "commentContent is required",
      );
    }

    const response = await http.post(
      "/retail/comment",
      {
        retailID:
          normalizedRetailID,

        userID:
          normalizedUserID,

        commentContent:
          normalizedCommentContent,
      },
    );

    return normalizeRetailCommentCreateResponse(
      response,
    );
  },

  /**
   * Cancel one Retail request owned by the supplied user.
   *
   * The backend permits cancellation only when the current status is:
   * checkedout
   *
   * Backend:
   * POST /wp-json/api/retail/cancel
   */
  async cancel(retailID, userID) {
    const normalizedRetailID =
      normalizeText(retailID);

    const normalizedUserID =
      normalizeWordPressUserID(userID);

    if (
      !normalizedRetailID ||
      !normalizedUserID
    ) {
      throw new Error(
        "retailID and userID are required",
      );
    }

    const response = await http.post(
      "/retail/cancel",
      {
        retailID: normalizedRetailID,
        userID: normalizedUserID,
      },
    );

    return normalizeRetailCancelResponse(
      response,
    );
  },
};

export default RetailAPI;